import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

const AttendanceSummary = ({ attendanceData, students, type = 'students' }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // Determine which summary to use based on type
  const summaryData = type === 'teachers' ? 
    (attendanceData?.teacherSummary || attendanceData?.studentSummary) : 
    attendanceData?.studentSummary;

  // Show "no data" message if no summary data
  if (!summaryData || summaryData?.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border">
        <div className="p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Monthly Summary</h3>
            <button
              onClick={toggleExpanded}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {isExpanded ? (
                <ChevronUp className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              )}
            </button>
          </div>
          {isExpanded && (
            <div className="mt-4 p-8 text-center">
              <div className="text-gray-400 mb-2">
                <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h4 className="text-lg font-medium text-gray-900 mb-2">No Summary Found</h4>
              <p className="text-gray-600">No attendance data available for the selected month{type === 'students' ? ' and class' : ''}.</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-[2rem] shadow-xl shadow-blue-900/5 border border-slate-100 overflow-hidden animate-slideInUp">
      <div className="p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-black text-slate-800 tracking-tight">Monthly Summary</h3>
            <p className="text-slate-400 text-sm font-medium mt-1">Attendance overview for all {type}</p>
          </div>
          <button
            onClick={toggleExpanded}
            className="p-3 bg-slate-50 hover:bg-slate-100 rounded-2xl transition-all border border-slate-100"
          >
            {isExpanded ? (
              <ChevronUp className="w-5 h-5 text-slate-600" />
            ) : (
              <ChevronDown className="w-5 h-5 text-slate-600" />
            )}
          </button>
        </div>
        
        {isExpanded && (
          <div className="mt-4">
            <div className="overflow-x-auto -mx-8">
              <table className="w-full border-separate border-spacing-y-2 px-8">
                <thead>
                  <tr className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                    <th className="text-left py-4 px-6">Name</th>
                    <th className="text-center py-4 px-6">Days</th>
                    <th className="text-center py-4 px-6">Present</th>
                    <th className="text-center py-4 px-6">Absent</th>
                    <th className="text-right py-4 px-6 pr-10">Percentage</th>
                  </tr>
                </thead>
                <tbody>
                  {summaryData?.map((summary, index) => {
                    const entity = students.find(s => s._id === summary?.studentId || s._id === summary?.teacherId);
                    const attendancePercentage = summary.totalDays > 0 
                      ? Math.round(((summary?.presentDays + (summary?.halfDays || 0) * 0.5) / summary?.totalDays) * 100)
                      : 0;
                    
                    return (
                      <tr 
                        key={summary?.studentId || summary?.teacherId} 
                        className="group hover:scale-[1.01] transition-all duration-300"
                      >
                        <td className="py-4 px-6 bg-slate-50/50 rounded-l-2xl border-y border-l border-slate-100/50 group-hover:bg-white group-hover:border-blue-100 transition-all">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center text-blue-600 font-black text-xs">
                              {entity?.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                            </div>
                            <span className="text-sm font-bold text-slate-900">{entity?.name || 'Unknown'}</span>
                          </div>
                        </td>
                        <td className="text-center py-4 px-6 bg-slate-50/50 border-y border-slate-100/50 group-hover:bg-white group-hover:border-blue-100 transition-all font-bold text-slate-600 text-sm">
                          {summary?.totalDays}
                        </td>
                        <td className="text-center py-4 px-6 bg-slate-50/50 border-y border-slate-100/50 group-hover:bg-white group-hover:border-blue-100 transition-all">
                          <span className="px-3 py-1 bg-emerald-50 text-emerald-600 rounded-lg font-black text-xs border border-emerald-100">
                            {summary?.presentDays}
                          </span>
                        </td>
                        <td className="text-center py-4 px-6 bg-slate-50/50 border-y border-slate-100/50 group-hover:bg-white group-hover:border-blue-100 transition-all">
                          <span className="px-3 py-1 bg-rose-50 text-rose-600 rounded-lg font-black text-xs border border-rose-100">
                            {summary?.absentDays}
                          </span>
                        </td>
                        <td className="py-4 px-6 pr-10 bg-slate-50/50 rounded-r-2xl border-y border-r border-slate-100/50 group-hover:bg-white group-hover:border-blue-100 transition-all">
                          <div className="flex flex-col items-end gap-1">
                            <span className={`text-sm font-black ${attendancePercentage >= 75 ? 'text-blue-600' : 'text-amber-500'}`}>
                              {attendancePercentage}%
                            </span>
                            <div className="w-20 h-1 bg-slate-200 rounded-full overflow-hidden">
                              <div 
                                className={`h-full rounded-full ${attendancePercentage >= 75 ? 'bg-blue-600' : 'bg-amber-500'}`}
                                style={{ width: `${attendancePercentage}%` }}
                              />
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AttendanceSummary;
